export default function WhyStudentsLove() {
  const reasons = [
    {
      icon: 'ri-award-line',
      emoji: '🎖',
      title: 'Instant Progress',
      description: 'See results from day one with quick badges that show your growth.',
      gradient: 'from-[#304FFE] to-[#5B9FED]',
    },
    {
      icon: 'ri-search-eye-line',
      emoji: '🔎',
      title: 'Real Clarity',
      description: 'Explore real skills before making important decisions about your future.',
      gradient: 'from-[#7C4DFF] to-[#B794F6]',
    },
    {
      icon: 'ri-heart-pulse-line',
      emoji: '💬',
      title: 'Confidence That Builds',
      description: 'Micro-wins create momentum and help you trust your direction.',
      gradient: 'from-[#FF6B9D] to-[#FFA726]',
    },
    {
      icon: 'ri-rocket-2-line',
      emoji: '🚀',
      title: 'Opportunities Through Merit',
      description: 'Unlock pathways, scholarships and advantages based on your progress.',
      gradient: 'from-[#00BFA5] to-[#00E676]',
    },
    {
      icon: 'ri-time-line',
      emoji: '⌛',
      title: 'Learning That Feels Natural',
      description: 'Short, practical, engaging microcourses designed for modern learners.',
      gradient: 'from-[#304FFE] to-[#7C4DFF]',
    },
  ];

  return (
    <section className="py-28 px-6 lg:px-12 relative overflow-hidden">
      {/* Animated gradient background - same as Hero */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#304FFE]/[0.08] via-transparent to-[#7C4DFF]/[0.08]">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
      </div>

      {/* Floating decorative elements - same as Hero */}
      <div className="absolute top-1/4 right-[10%] w-64 h-64 bg-gradient-to-br from-[#304FFE]/20 to-[#7C4DFF]/20 rounded-full blur-3xl animate-pulse"></div>
      <div className="absolute bottom-1/4 left-[15%] w-48 h-48 bg-gradient-to-br from-[#7C4DFF]/15 to-[#304FFE]/15 rounded-full blur-3xl animate-pulse delay-1000"></div>

      {/* Enhanced Mosaic Background with Student Photos */}
      <div className="absolute inset-0 opacity-100 pointer-events-none">
        {/* Dense Photo Grid Container - 18 photos in uniform grid */}
        <div className="absolute inset-0 grid grid-cols-6 grid-rows-3 gap-4 p-8">
          {/* Row 1 */}
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://public.readdy.ai/ai/img_res/7665baf37de9a4710f20f0c7870350c8.jpg" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20professional%20woman%20working%20on%20tablet%20smiling%20confident%20modern%20workspace%20bright%20natural%20light%20business%20casual%20attire%20positive%20energy%20vibrant%20cheerful%20atmosphere&width=400&height=400&seq=student2&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=group%20of%20diverse%20students%20collaborating%20on%20project%20teamwork%20happy%20engaged%20learning%20together%20modern%20classroom%20bright%20environment%20multicultural%20education%20vibrant%20colors&width=400&height=400&seq=student3&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=male%20student%20celebrating%20success%20achievement%20happy%20excited%20fist%20pump%20victory%20gesture%20bright%20smile%20casual%20clothing%20positive%20energy%20educational%20success%20vibrant%20atmosphere&width=400&height=400&seq=student4&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20woman%20using%20smartphone%20learning%20app%20smiling%20engaged%20digital%20education%20modern%20technology%20bright%20natural%20light%20casual%20style%20vibrant%20cheerful%20atmosphere&width=400&height=400&seq=student5&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=diverse%20students%20studying%20together%20in%20coffee%20shop%20laptops%20books%20collaborative%20learning%20happy%20engaged%20modern%20casual%20environment%20bright%20natural%20lighting%20vibrant%20colors&width=400&height=400&seq=student6&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>

          {/* Row 2 */}
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20professional%20presenting%20ideas%20confident%20gesture%20modern%20workspace%20bright%20environment%20business%20casual%20attire%20positive%20energy%20success%20mindset%20vibrant%20atmosphere&width=400&height=400&seq=student7&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=students%20working%20on%20creative%20project%20design%20thinking%20brainstorming%20happy%20collaborative%20modern%20workspace%20bright%20colorful%20environment%20engaged%20learning%20vibrant&width=400&height=400&seq=student8&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20man%20reading%20book%20smiling%20happy%20engaged%20learning%20casual%20clothing%20bright%20natural%20light%20educational%20setting%20positive%20atmosphere%20vibrant%20cheerful&width=400&height=400&seq=student9&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=diverse%20group%20of%20students%20celebrating%20achievement%20high%20five%20happy%20success%20teamwork%20modern%20campus%20bright%20environment%20multicultural%20education%20positive%20energy%20vibrant&width=400&height=400&seq=student10&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=female%20student%20using%20laptop%20smiling%20confident%20modern%20library%20bright%20natural%20light%20casual%20clothing%20engaged%20learning%20positive%20attitude%20educational%20success%20vibrant%20atmosphere&width=400&height=400&seq=student11&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20diverse%20students%20taking%20notes%20in%20class%20attentive%20focused%20learning%20modern%20classroom%20bright%20environment%20educational%20setting%20vibrant%20cheerful%20atmosphere&width=400&height=400&seq=student12&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>

          {/* Row 3 */}
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=male%20student%20working%20on%20computer%20coding%20programming%20happy%20engaged%20modern%20tech%20workspace%20bright%20natural%20light%20casual%20attire%20vibrant%20atmosphere&width=400&height=400&seq=student13&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20woman%20presenting%20project%20confident%20speaking%20gesture%20modern%20classroom%20bright%20environment%20business%20casual%20positive%20energy%20success%20vibrant%20cheerful&width=400&height=400&seq=student14&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=diverse%20students%20group%20discussion%20circle%20engaged%20conversation%20learning%20together%20modern%20campus%20outdoor%20bright%20natural%20light%20multicultural%20vibrant%20atmosphere&width=400&height=400&seq=student15&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20professional%20using%20tablet%20digital%20learning%20smiling%20happy%20modern%20workspace%20bright%20environment%20casual%20clothing%20positive%20energy%20vibrant%20cheerful%20atmosphere&width=400&height=400&seq=student16&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=students%20collaborating%20on%20whiteboard%20brainstorming%20ideas%20teamwork%20happy%20engaged%20modern%20classroom%20bright%20colorful%20environment%20vibrant%20educational%20setting&width=400&height=400&seq=student17&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
          <div className="rounded-2xl overflow-hidden">
            <img 
              src="https://readdy.ai/api/search-image?query=young%20student%20celebrating%20with%20arms%20raised%20victory%20success%20achievement%20happy%20excited%20bright%20smile%20casual%20clothing%20positive%20energy%20vibrant%20cheerful%20atmosphere&width=400&height=400&seq=student18&orientation=squarish" 
              alt="" 
              className="w-full h-full object-cover object-top saturate-[0.7] blur-[2px]"
            />
          </div>
        </div>

        {/* Brand-colored gradient overlay (blue to purple) - subtle */}
        <div className="absolute inset-0 bg-gradient-to-br from-[#304FFE]/15 via-[#5B7FFF]/10 to-[#7C4DFF]/15"></div>

        {/* White gradient overlay for readability */}
        <div className="absolute inset-0 bg-gradient-to-b from-white/90 via-white/75 to-white/70"></div>
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <div className="text-center mb-20">
          <h2 className="text-5xl lg:text-6xl font-bold text-[#1A1F36] mb-6">
            Why students love Praxia
          </h2>
          <p className="text-2xl text-gray-600 max-w-3xl mx-auto font-light">
            Learning that motivates, empowers and moves you forward.
          </p>
        </div>

        {/* Cards - Single Row Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
          {reasons.map((reason, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100 relative overflow-hidden flex flex-col"
            >
              {/* Subtle background gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${reason.gradient} opacity-[0.02] group-hover:opacity-[0.08] transition-opacity duration-500`}></div>
              
              {/* Content */}
              <div className="relative z-10 flex flex-col h-full">
                {/* Icon with gradient background */}
                <div className={`w-16 h-16 bg-gradient-to-br ${reason.gradient} rounded-2xl flex items-center justify-center mb-5 group-hover:scale-110 group-hover:rotate-6 transition-all duration-500 shadow-lg`}>
                  <i className={`${reason.icon} text-3xl text-white`}></i>
                </div>

                {/* Title with emoji */}
                <div className="mb-3">
                  <h3 className="text-xl font-bold text-[#1A1F36] mb-1 leading-tight">
                    {reason.emoji} {reason.title}
                  </h3>
                </div>

                {/* Description */}
                <p className="text-sm text-gray-600 leading-relaxed flex-grow">
                  {reason.description}
                </p>

                {/* Decorative accent line */}
                <div className={`w-16 h-1 bg-gradient-to-r ${reason.gradient} rounded-full mt-5 group-hover:w-24 transition-all duration-500`}></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
